import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Modal,
  Stack,
  TextField,
  Typography,
  Box,
  FormControl,
  FormLabel,
  IconButton,
  InputLabel,
  InputAdornment,
  MenuItem,
  Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import { roleModulePermissions } from "../../utils/roleInstance";
import FilterList from "@mui/icons-material/FilterList";

const ButtonStyle = {
  width: "100%",
  height: "50px",
  padding: "10px 10px 10px 10px",
  boxShadow: "0px 4px 13px rgba(0, 0, 0, 0.25)",
  borderRadius: "20px",
  backgroundColor: "#FE9A25",
  color: "#1E0D61",
  fontSize: "15px",
  fontWeight: "500",
  textTransform: "none",
};

const SearchFeild = (props) => {
  // 1. Log all incoming props on every render
  //console.log("🔍 [SearchField] Component Rendered. Current Props:", props);

  const {
    zohoButtonName,
    onZohoClick,
    showZohoButton = false,
    isLoadingZoho = false,
    dataSourceFilter,
    onFilterChange,
    showFilter = false,
  } = props;

  // Initialize state
  const initialPermissionState = !props.roleModule;
  //console.log(`🎬 [SearchField] Initializing questionAction state to: ${initialPermissionState} (Based on roleModule: "${props.roleModule}")`);
  
  const [questionAction, setQuestionAction] = useState(initialPermissionState);

  // 2. Log state changes
  //console.log(`📊 [SearchField] Current questionAction state value:`, questionAction);

  const checkQuestionAction = async () => {
    //console.log(`🏃‍♂️ [SearchField] checkQuestionAction() triggered for module: "${props.roleModule}"`);

    if (!props.roleModule) {
      //console.warn("⚠️ [SearchField] checkQuestionAction aborted: No roleModule provided in props. Enabling button by default.");
      setQuestionAction(true);
      return;
    }

    try {
      const roleData = { roleModule: props.roleModule };
      //console.log("📡 [SearchField] Sending API Request to roleModulePermissions.checkUserRoles with payload:", roleData);
      
      let rolePermission = await roleModulePermissions.checkUserRoles(roleData, "add");
      
      //console.log(`✅ [SearchField] API Response received for "${props.roleModule}":`, rolePermission);
      //console.log(`💡 [SearchField] Type of response: ${typeof rolePermission}`);

      // Boolean casting check
      const castedBoolean = !!rolePermission;
      //  console.log(`🔄 [SearchField] Casting response to strict boolean: !!(${rolePermission}) === ${castedBoolean}`);
      
      setQuestionAction(castedBoolean); 
    } catch (error) {
      //console.error("❌ [SearchField] CRITICAL ERROR during checkUserRoles API call:", error);
      //console.log("🔒 [SearchField] Falling back to disabling button due to error catch.");
      setQuestionAction(false); 
    }
  };

  // 3. Log Hook Lifecycle
  useEffect(() => {
    //console.log(`🔄 [SearchField] useEffect triggered because props.roleModule changed to: "${props.roleModule}"`);
    checkQuestionAction();
  }, [props.roleModule]);

  return (
    <>
      <Stack
        sx={{
          width: props.width,
          margin: "auto",
          mt: { lg: props.large, md: props.medium, sm: props.medium, xs: props.xtraSmall },
          mb: props.marginBottom || "10px",
        }}
        direction={{ lg: "row", md: "row", sm: "row", xs: "column" }}
        spacing={{ lg: 0.5, md: 0.5, sm: 1, xs: 1 }}
        justifyContent="space-between"
      >
        {/* Search Field */}
        <Box sx={{ width: { lg: props.TextFieldWidth, md: props.TextFieldWidth, sm: props.TextFieldWidth, xs: "100%" } }}>
          <TextField
            sx={{ width: { xs: "100%", sm: "50%", md: "50%" } }}
            variant="outlined"
            type="text"
            placeholder={props.placeholder}
            onChange={(e) => {
              //console.log("✏️ [SearchField] Search text changed:", e.target.value);
              if (props.onChange) props.onChange(e);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon style={{ color: "#CAC2C2" }} />
                  </IconButton>
                </InputAdornment>
              ),
              style: { borderRadius: "12px", height: "50px", padding: 2 },
            }}
          />
        </Box>

        {/* Filter List */}
        {showFilter && (
          <Box sx={{ width: { lg: "auto", md: "auto", sm: "auto", xs: "100%" }, order: { lg: 2, md: 2, sm: 2, xs: 2 } }}>
            <FormControl size="small" sx={{ minWidth: 10 }}>
              <Select
                id="data-source-filter"
                value={dataSourceFilter}
                onChange={(e) => {
                  //console.log("🎯 [SearchField] Filter selection changed:", e.target.value);
                  if (onFilterChange) onFilterChange(e);
                }}
                sx={{ "& .MuiSelect-icon": { display: "none" } }}
                IconComponent={() => null}
                renderValue={() => <FilterList sx={{ fontSize: '18px' }} />}
              >
                <MenuItem value="all">All Jobs</MenuItem>
                <MenuItem value="zoho">Zoho Jobs</MenuItem>
                <MenuItem value="manual">Manual Jobs</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Pull Zoho Button */}
        {showZohoButton && (
          <Box sx={{ width: { lg: "auto", md: "auto", sm: "auto", xs: "100%" }, mt: { sm: 1, xs: 1 } }}>
            <Button
              sx={{
                ...ButtonStyle,
                width: { sm: "100%", xs: "100%", lg: "auto" },
                backgroundColor: "secondary.main",
                color: "primary.main",
                "&:hover": { backgroundColor: "secondary.dark" },
              }}
              onClick={(e) => {
                //console.log("🤖 [SearchField] Zoho button clicked.");
                if (onZohoClick) onZohoClick(e);
              }}
              disabled={isLoadingZoho}
              startIcon={<SyncIcon fontSize="large" />}
            >
              {isLoadingZoho ? "Extracting..." : zohoButtonName}
            </Button>
          </Box>
        )}

        {/* Add Button */}
        <Box sx={{ width: { lg: "auto", md: "auto", sm: "auto", xs: "100%" }, mt: { sm: 1, xs: 1 } }}>
          {/* Final sanity check right at the injection layer */}
          {(() => {
            //console.log(`🚨 [SearchField RENDERING BUTTON] Final UI Evaluation -> disabled={${!questionAction}} | onClick calls: ${questionAction ? 'props.Navigate' : 'null'}`);
            return null;
          })()}
          <Button
            sx={{
              ...ButtonStyle,
              width: { sm: "100%", xs: "100%", lg: "auto" },
              backgroundColor: "secondary.main",
              color: "primary.main",
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
            onClick={() => {
              //console.log("👆 [SearchField] Add Button clicked! Executing handler...");
              if (questionAction && props.Navigate) props.Navigate();
            }}
            disabled={!questionAction}
          >
            <AddIcon fontSize="large" /> {props.buttonName}
          </Button>
        </Box>
      </Stack>
    </>
  );
};

export default SearchFeild;